import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
export const Route = createFileRoute('/_layout/blog/feedback-campaigns/')({
    component: FeedbackCampaignsPost,
    head: () => ({
        meta: [
            { title: 'Introducing Feedback Campaigns — Adora' },
            {
                name: 'description',
                content: 'Turn any QR or smart link into a feedback form. Submissions land in Telegram in real time and stay attributed to the scan that produced them.',
            },
            { property: 'og:title', content: 'Introducing Feedback Campaigns — Adora' },
            {
                property: 'og:description',
                content: 'Turn any QR or smart link into a feedback form. Submissions land in Telegram in real time and stay attributed to the scan that produced them.',
            },
        ],
    }),
});
function FeedbackCampaignsPost() {
    return (<article className="mx-auto max-w-3xl px-6 md:px-8 py-12">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5"/>
                All posts
            </Link>

            <header className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-wide text-primary">Announcement · 2026-04-28</p>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Introducing Feedback Campaigns</h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    Turn any QR or smart link into a public feedback form. Submissions land in your Telegram chat in
                    real time, attributed to the exact scan that produced them.
                </p>
            </header>

            <div className="prose prose-neutral dark:prose-invert mt-10 max-w-none">
                <h2>Why we built it</h2>
                <p>
                    Most QR campaigns die at "scan count". Counts tell you that 1,200 people picked up a flyer, but not
                    what they thought of the place. The whole point of Adora is closing that loop:
                    <strong> physical attention → measurable intent</strong>. Feedback is the next signal after the
                    click.
                </p>
                <p>
                    Until today, you could see <em>where</em> the scan came from (tuk-tuk #4, table 7, packaging batch
                    A) and <em>which destination</em> someone clicked. Now you can also collect what they actually felt
                    about the experience — without setting up a separate survey tool, embedding Typeform on a landing
                    page, or asking customers to install anything.
                </p>

                <h2>How it works</h2>
                <ol>
                    <li>
                        Create a campaign with mode <code>feedback</code>. The same QR / smart-link infrastructure you
                        already use — just a different routing mode.
                    </li>
                    <li>
                        A scan lands on a public form (rating + comment + optional name and email). Visitors don't log
                        in and don't install anything. The form is mobile-first with a sticky submit and 16px+ font
                        sizes (no iOS zoom-on-focus).
                    </li>
                    <li>
                        On submit, Adora stores the feedback against the originating scan — the same record that knows
                        the QR's geo, device, and context label. Then it pings your Telegram chat with the formatted
                        message.
                    </li>
                </ol>

                <h2>Where notifications go</h2>
                <p>Per-workspace default plus per-campaign override. Two places to set the chat ID:</p>
                <ul>
                    <li>
                        <strong>Workspace setting</strong> — your default. Set once in
                        <code>/console/workspace</code>; every feedback campaign uses it unless overridden.
                    </li>
                    <li>
                        <strong>Campaign override</strong> — useful when one specific campaign should fire to a
                        different chat (e.g. a per-restaurant location group, or a customer-specific stream).
                    </li>
                </ul>
                <p>
                    Resolution at delivery time: campaign override → workspace default → skip with a warning log (and
                    the admin list flags <code>delivered = false</code> so you can see it).
                </p>

                <h2>Attribution stays intact</h2>
                <p>
                    Every feedback submission stays linked to the scan it came from, and through it to the specific
                    smart link that produced it — so every rating inherits the scan&apos;s geo, device, and context
                    label. You can answer questions like &ldquo;what was the average rating from the QR on the
                    chili-oil packaging this week?&rdquo; directly in the dashboard, no joins required.
                </p>

                <h2>What's next</h2>
                <ul>
                    <li>Per-campaign field configuration (custom questions, multiple-choice).</li>
                    <li>Async delivery via the message queue so the public submit returns instantly.</li>
                    <li>CAPTCHA / Turnstile integration for high-traffic campaigns.</li>
                    <li>Reply-to-submission via Telegram bot to close the loop end-to-end.</li>
                </ul>
            </div>

            <footer className="mt-12 border-t pt-6 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5"/>
                    All posts
                </Link>
                <Link to="/sign-up" search={{ redirect: '/console' }} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
                    Try it free
                    <ArrowRight className="h-4 w-4"/>
                </Link>
            </footer>
        </article>);
}
